import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';
import { useAuth } from '@/store/auth';

import type { AuthSession } from '@logistech/ui-shared';

export const Route = createFileRoute('/login')({ component: LoginPage });

/** BUG-031 : une coupure réseau, un 429 ou un 5xx ne doivent pas passer pour un mauvais mot de passe. */
export const loginErrorMessage = (err: unknown): string => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === undefined) return 'Connexion impossible, vérifie ton réseau';
  if (status === 401) return 'Identifiants invalides';
  if (status === 429) return 'Trop de tentatives, réessaie dans quelques minutes';
  if (status >= 500) return 'Service momentanément indisponible';
  return 'Erreur de connexion';
};

type Role = AuthSession['user']['role'];

/** BUG-021 : console réservée à l'équipe bureau ; DRIVER (PWA chauffeur) et API_PARTNER (public-api) refusés. */
const DASHBOARD_ROLES: ReadonlySet<Role> = new Set(['OWNER', 'ADMIN', 'MANAGER', 'DISPATCHER', 'ACCOUNTANT', 'VIEWER']);

export const roleRefusedMessage = (role: Role): string | null => {
  if (DASHBOARD_ROLES.has(role)) return null;
  return role === 'DRIVER'
    ? "Cette application est réservée à l'équipe bureau. Les chauffeurs utilisent l'app PWA."
    : "Ce compte n'a pas accès à la console de gestion.";
};

function LoginPage() {
  const nav = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const logout = useAuth((s) => s.logout);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<AuthSession>('/auth/login', { email, password });
      const refused = roleRefusedMessage(res.data.user.role);
      if (refused) {
        await logout();
        setError(refused);
        return;
      }
      setSession(res.data);
      void nav({ to: '/dashboard' });
    } catch (err) {
      setError(loginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-900 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-primary-500">LogisTech-LGT</CardTitle>
          <p className="text-sm text-muted-foreground">Console de gestion</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            {error && <p className="text-sm text-danger">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Connexion…' : 'Se connecter'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
